/**
 * useMemberFavorites
 * --------------------------------------------------------------------------
 * Shared hook for the staff-side "favorite members" feature.
 * Wraps the existing /api/members/favorites/* endpoints (used to live only
 * inside EnrollmentListNew). Now used by MemberListNew and MemberProfileNew
 * so favoriting works from anywhere a member is shown.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/services/apiClient';

export interface FavoriteMemberDetail {
  member_id: number;
  member_name?: string;
  // Other fields (enrollment_status, program_name, assigned_name, …) are
  // returned by the backend but only used by the favorites strip.
  [key: string]: any;
}

export function useMemberFavorites() {
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(new Set());
  const [favoritesDetail, setFavoritesDetail] = useState<FavoriteMemberDetail[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchFavorites = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiClient.get('/api/members/favorites/list');
      setFavoriteIds(new Set(resp.data?.favorite_member_ids || []));
      setFavoritesDetail(resp.data?.favorites || []);
    } catch {
      // Silently ignore — favorites are optional UX; never block the page.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const isFavorite = useCallback((memberId: number) => favoriteIds.has(memberId), [favoriteIds]);

  const toggleFavorite = useCallback(
    async (memberId: number) => {
      const wasFav = favoriteIds.has(memberId);
      // Optimistic update for snappy UI; rollback on error.
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (wasFav) next.delete(memberId);
        else next.add(memberId);
        return next;
      });
      try {
        if (wasFav) {
          await apiClient.delete(`/api/members/favorites/${memberId}`);
        } else {
          await apiClient.post(`/api/members/favorites/${memberId}`);
        }
        // Refresh details for the strip (server is source of truth for joined fields).
        fetchFavorites();
      } catch {
        // Rollback on failure.
        setFavoriteIds(prev => {
          const next = new Set(prev);
          if (wasFav) next.add(memberId);
          else next.delete(memberId);
          return next;
        });
      }
    },
    [favoriteIds, fetchFavorites],
  );

  return {
    favoriteIds,
    favoritesDetail,
    loading,
    isFavorite,
    toggleFavorite,
    refresh: fetchFavorites,
  };
}
